import {
  AlertCircle,
  Archive,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Download,
  HardHat,
  LoaderCircle,
} from "lucide-react";
import React from "react";
import { Link } from "react-router-dom";
import { getApiErrorMessage, portalApi } from "../api/client";
import type { ApiEnvelope } from "../api/types";
import { RequirementRow } from "../compliance/RequirementRow";
import { PageError } from "../components/PageError";
import type {
  ComplianceGroup,
  DocumentationSection,
  PortalDocumentation,
} from "../types/compliance";
import { Badge, Button } from "../components/ui";

type PageState =
  | { status: "loading" }
  | { status: "forbidden" }
  | { status: "error"; message: string }
  | { status: "ready"; data: PortalDocumentation };

const descriptions: Record<DocumentationSection["id"], string> = {
  resolve_now: "Documentos vencidos, ausentes ou reprovados.",
  expiring_soon: "Antecipe o envio para evitar interrupções.",
  review: "Já recebemos estes arquivos. Nenhuma ação é necessária agora.",
  completed: "Documentos obrigatórios atendidos e vigentes.",
  optional:
    "Estes documentos enriquecem seu cadastro, mas não são obrigatórios.",
};

export function DocumentationPage() {
  const [state, setState] = React.useState<PageState>({ status: "loading" });
  const [openSections, setOpenSections] = React.useState<Set<string>>(
    new Set(["resolve_now", "expiring_soon", "review"])
  );
  const [downloading, setDownloading] = React.useState(false);
  const [downloadError, setDownloadError] = React.useState("");

  const load = React.useCallback(async () => {
    setState({ status: "loading" });
    try {
      const response = await portalApi.get<ApiEnvelope<PortalDocumentation>>(
        "/portal/documentation"
      );
      setState({ status: "ready", data: response.data.data });
    } catch (requestError: any) {
      if (requestError.response?.status === 403) {
        setState({ status: "forbidden" });
      } else {
        setState({
          status: "error",
          message: getApiErrorMessage(
            requestError,
            "Não foi possível carregar a documentação."
          ),
        });
      }
    }
  }, []);

  React.useEffect(() => {
    void load();
  }, [load]);

  function toggle(section: string) {
    setOpenSections((current) => {
      const next = new Set(current);
      if (next.has(section)) next.delete(section);
      else next.add(section);
      return next;
    });
  }

  async function downloadAll() {
    setDownloading(true);
    setDownloadError("");
    try {
      const response = await portalApi.get<Blob>(
        "/portal/requirements/compliance/download",
        { responseType: "blob" }
      );
      if (response.status === 204 || response.data.size === 0) {
        setDownloadError("Ainda não há documentos atuais para baixar.");
        return;
      }
      const url = URL.createObjectURL(response.data);
      const link = document.createElement("a");
      link.href = url;
      link.download = "documentos-atuais.zip";
      link.click();
      window.setTimeout(() => URL.revokeObjectURL(url), 60_000);
    } catch (requestError) {
      setDownloadError(
        getApiErrorMessage(requestError, "Não foi possível gerar o download.")
      );
    } finally {
      setDownloading(false);
    }
  }

  if (state.status === "loading") {
    return (
      <div className="space-y-5" aria-label="Carregando documentação">
        <div className="h-20 w-80 animate-pulse rounded-xl bg-slate-200" />
        {[1, 2, 3].map((item) => (
          <div
            key={item}
            className="h-28 animate-pulse rounded-2xl bg-slate-200"
          />
        ))}
      </div>
    );
  }

  if (state.status === "forbidden") {
    return (
      <PageError
        title="Acesso não permitido"
        description="Seu perfil não pode visualizar a documentação desta empresa."
      />
    );
  }

  if (state.status === "error") {
    return (
      <PageError
        title="Não foi possível carregar"
        description={state.message}
        retry={() => void load()}
      />
    );
  }

  const { data } = state;
  const empty =
    data.summary.required.total === 0 &&
    data.summary.optional.total === 0 &&
    data.discontinued.count === 0;

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <p className="text-sm font-medium text-teal-700">Conformidade</p>
          <h1 className="mt-1 text-2xl font-semibold tracking-tight text-slate-950">
            Documentação
          </h1>
          <p className="mt-2 max-w-2xl text-sm leading-6 text-slate-600">
            Veja o que precisa ser enviado, o que está em análise e os
            documentos já concluídos.
          </p>
        </div>
        <Button
          type="button"
          variant="outline"
          onClick={() => void downloadAll()}
          disabled={downloading}
        >
          {downloading ? (
            <LoaderCircle className="animate-spin" size={17} />
          ) : (
            <Download size={17} />
          )}
          Baixar documentos atuais
        </Button>
      </header>

      {downloadError && (
        <div className="portal-message portal-message-error" role="alert">
          <AlertCircle size={18} />
          <p>{downloadError}</p>
        </div>
      )}

      {empty ? (
        <section className="rounded-2xl border border-teal-200 bg-teal-50 p-8 text-center">
          <CheckCircle2 className="mx-auto text-teal-700" size={30} />
          <h2 className="mt-3 text-lg font-semibold text-teal-950">
            Nenhum documento solicitado
          </h2>
          <p className="mx-auto mt-1 max-w-md text-sm text-teal-800">
            Sua empresa ainda não possui requerimentos documentais neste portal.
            Quando houver uma solicitação, ela aparecerá aqui.
          </p>
        </section>
      ) : (
        <>
          {data.sections.map((section) => (
            <DocumentationSectionCard
              key={section.id}
              section={section}
              open={openSections.has(section.id)}
              onToggle={() => toggle(section.id)}
              onChanged={load}
            />
          ))}

          {data.assets && <AssetSection group={data.assets} />}

          {data.discontinued.count > 0 && (
            <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
              <Button
                type="button"
                variant="ghost"
                className="h-auto w-full justify-start rounded-none p-4 text-left"
                onClick={() => toggle("discontinued")}
              >
                <Archive className="text-slate-500" size={20} />
                <div className="flex-1">
                  <h2 className="font-semibold text-slate-800">
                    Descontinuados
                  </h2>
                  <p className="text-sm text-slate-500">
                    {data.discontinued.count} documentos mantidos apenas para
                    consulta.
                  </p>
                </div>
                {openSections.has("discontinued") ? (
                  <ChevronDown size={18} />
                ) : (
                  <ChevronRight size={18} />
                )}
              </Button>
              {openSections.has("discontinued") && (
                <div className="border-t border-slate-200">
                  {data.discontinued.requirements.map((requirement) => (
                    <RequirementRow
                      key={requirement.cdreq}
                      requirement={requirement}
                      onChanged={load}
                    />
                  ))}
                </div>
              )}
            </section>
          )}
        </>
      )}
    </div>
  );
}

function DocumentationSectionCard({
  section,
  open,
  onToggle,
  onChanged,
}: {
  section: DocumentationSection;
  open: boolean;
  onToggle(): void;
  onChanged(): Promise<void>;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm">
      <Button
        type="button"
        variant="ghost"
        className="h-auto w-full justify-start rounded-none p-4 text-left sm:p-5"
        onClick={onToggle}
      >
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-semibold text-slate-900">{section.label}</h2>
            <span className="rounded-full bg-slate-100 px-2.5 py-1 text-xs font-medium text-slate-600">
              {section.count}
            </span>
          </div>
          <p className="mt-1 text-sm text-slate-600">
            {descriptions[section.id]}
          </p>
        </div>
        {open ? <ChevronDown size={19} /> : <ChevronRight size={19} />}
      </Button>
      {open && (
        <div className="border-t border-slate-200">
          {section.requirements.length === 0 ? (
            <p className="p-5 text-sm text-slate-600">
              Não há documentos desta seção no cadastro da empresa ou nos
              serviços. Pendências de ativos aparecem no bloco específico
              abaixo.
            </p>
          ) : (
            section.requirements.map((requirement) => (
              <RequirementRow
                key={requirement.cdreq}
                requirement={requirement}
                onChanged={onChanged}
              />
            ))
          )}
        </div>
      )}
    </section>
  );
}

function AssetSection({ group }: { group: ComplianceGroup }) {
  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="flex items-start gap-3">
        <div className="flex size-10 shrink-0 items-center justify-center rounded-xl bg-slate-100 text-slate-600">
          <HardHat size={20} />
        </div>
        <div>
          <h2 className="font-semibold text-slate-900">
            Documentos dos ativos
          </h2>
          <p className="mt-1 text-sm text-slate-600">
            Pendências agrupadas por tipo de ativo vinculado à empresa.
          </p>
        </div>
      </div>
      <div className="mt-4 grid gap-3 md:grid-cols-2">
        {(group.children ?? []).map((assetType) => (
          <Link
            key={assetType.cdsubjecttype}
            to={`/assets/${assetType.cdsubjecttype}`}
            className="rounded-xl border border-slate-200 p-4"
          >
            <div className="flex items-start justify-between gap-3">
              <div>
                <h3 className="font-medium text-slate-900">
                  {assetType.nmsubjecttype || "Ativos"}
                </h3>
                <p className="mt-1 text-xs text-slate-500">
                  {assetType.nrsubjects ?? 0} ativos vinculados
                </p>
              </div>
              {assetType.fgmatrix && (
                <Badge
                  variant="outline"
                  className="rounded-full border-slate-200 bg-slate-50 text-slate-600"
                >
                  Visão agrupada
                </Badge>
              )}
            </div>
            <div className="mt-4 grid grid-cols-3 gap-2 text-center">
              <AssetMetric
                label="Resolver"
                value={assetType.summary.critical}
                tone="text-red-700"
              />
              <AssetMetric
                label="Em breve"
                value={assetType.summary.warning}
                tone="text-amber-700"
              />
              <AssetMetric
                label="Atendidos"
                value={`${assetType.summary.required.met}/${assetType.summary.required.total}`}
                tone="text-teal-700"
              />
            </div>
            <span className="mt-4 inline-flex text-xs font-semibold text-teal-700">
              Ver todos os ativos
            </span>
          </Link>
        ))}
      </div>
    </section>
  );
}

function AssetMetric({
  label,
  value,
  tone,
}: {
  label: string;
  value: React.ReactNode;
  tone: string;
}) {
  return (
    <div className="rounded-lg bg-slate-50 px-2 py-3">
      <strong className={`block text-lg ${tone}`}>{value}</strong>
      <span className="text-[11px] text-slate-500">{label}</span>
    </div>
  );
}
